import { Accordion, AccordionDetails, AccordionSummary, Button, Divider, Stack, styled, Text } from 'shell/components';
import {
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
} from 'utils/constants';

const getSuggestionLengthText = (suggestionLength) => {
    return {
        '<50': 'Under 150',
        '50-100': '150-300',
        '100-150': '300-450',
        '150-200': '450-600',
    }[suggestionLength];
};

const getLanguageLevelText = (languageLevel) => {
    return {
        'middle-school': 'Middle School',
        'high-school': 'High School',
        undergraduate: 'Undergraduate',
        graduate: 'Graduate',
    }[languageLevel];
};

const getToneText = (tone) => {
    return {
        'very-supportive': 'Very Supportive',
        supportive: 'Supportive',
        balanced: 'Balanced',
        critical: 'Critical',
    }[tone];
};

const hasCustomGradingSettings = (assignment) => {
    return Boolean(assignment.ai_grading_settings) || Boolean(assignment.ai_grading_context);
};

const AssignmentSettings = ({ assignment, onClickViewGradingSettings }) => {
    return (
        <>
            <Stack divider={<Divider />} spacing={2}>
                <Text>Feedback</Text>
                <Stack spacing={2}>
                    <Stack alignItems={'center'} direction={'row'} spacing={1}>
                        <Text>Final Submission Feedback Suggestions:</Text>
                        <Text color={'light'}>{assignment.ai_feedback_enabled ? 'Enabled' : 'Not Enabled'}</Text>
                    </Stack>
                    <Stack alignItems={'center'} direction={'row'} spacing={1}>
                        <Text>Instant Feedback for Students:</Text>
                        <Text color={'light'}>
                            {assignment.ai_student_feedback_enabled
                                ? 'Enabled'
                                : assignment.ai_feedback_settings?.structure === 'templated'
                                  ? 'Not Available'
                                  : 'Not Enabled'}
                        </Text>
                    </Stack>
                    <Accordion>
                        <AccordionSummary sx={{ width: 'fit-content' }}>
                            <Text>Advanced Feedback Settings</Text>
                        </AccordionSummary>
                        <AccordionDetails sx={{ mt: 2 }}>
                            <CustomStack spacing={3}>
                                <Stack>
                                    <Text color={'light'}>
                                        Advanced settings:{' '}
                                        {assignment.ai_feedback_settings?.structure === 'rubric' &&
                                            'Rubric based feedback'}
                                        {assignment.ai_feedback_settings?.structure === 'system' &&
                                            'General & detailed feedback'}
                                        {assignment.ai_feedback_settings?.structure === 'templated' &&
                                            'Smart Feedback Templates'}
                                        {assignment.ai_feedback_settings?.structure === 'custom' &&
                                            'Custom feedback structure'}
                                    </Text>
                                    {['rubric', 'system'].includes(assignment.ai_feedback_settings?.structure) && (
                                        <Stack sx={{ m: 0, listStyleType: '"- "' }} component={'ul'}>
                                            {assignment.ai_feedback_settings?.structure === 'system' && (
                                                <Text color={'light'} component={'li'}>
                                                    Number of suggestions:{' '}
                                                    {assignment.ai_feedback_settings?.num_suggestions ||
                                                        DEFAULT_FEEDBACK_NUM_SUGGESTIONS}
                                                </Text>
                                            )}
                                            <Text color={'light'} component={'li'}>
                                                Length of each suggestion:{' '}
                                                {getSuggestionLengthText(
                                                    assignment.ai_feedback_settings?.suggestion_length ||
                                                        DEFAULT_FEEDBACK_SUGGESTION_LENGTH
                                                )}{' '}
                                                words
                                            </Text>
                                            <Text color={'light'} component={'li'}>
                                                Language level:{' '}
                                                {getLanguageLevelText(
                                                    assignment.ai_feedback_settings?.language_level ||
                                                        DEFAULT_FEEDBACK_LANGUAGE_LEVEL
                                                )}
                                            </Text>
                                            <Text color={'light'} component={'li'}>
                                                Feedback tone:{' '}
                                                {getToneText(
                                                    assignment.ai_feedback_settings?.tone || DEFAULT_FEEDBACK_TONE
                                                )}
                                            </Text>
                                        </Stack>
                                    )}
                                </Stack>
                                {['rubric', 'system'].includes(assignment.ai_feedback_settings?.structure) && (
                                    <Text color={'light'}>
                                        Additional instructions:
                                        {assignment.ai_feedback_context ? (
                                            <>
                                                <Text color={'light'} component={'i'} rich>
                                                    {assignment.ai_feedback_context}
                                                </Text>
                                            </>
                                        ) : (
                                            <Text color={'light'} component={'i'}>
                                                {' '}
                                                None
                                            </Text>
                                        )}
                                    </Text>
                                )}
                                {assignment.ai_feedback_settings?.structure === 'custom' && (
                                    <Text color={'light'} rich>
                                        {assignment.ai_feedback_settings?.custom_instructions}
                                    </Text>
                                )}
                            </CustomStack>
                        </AccordionDetails>
                    </Accordion>
                </Stack>
            </Stack>
            <Stack divider={<Divider />} spacing={2}>
                <Text>Grading</Text>
                <Stack spacing={2}>
                    <Stack alignItems={'center'} direction={'row'} spacing={1}>
                        <Text>Final Submission Grading Suggestions:</Text>
                        <Text color={'light'}>{assignment.ai_grading_enabled ? 'Enabled' : 'Not Enabled'}</Text>
                    </Stack>
                    <Accordion>
                        <AccordionSummary sx={{ width: 'fit-content' }}>
                            <Text>Advanced Grading Settings</Text>
                        </AccordionSummary>
                        <AccordionDetails sx={{ mt: 2 }}>
                            <CustomStack spacing={3}>
                                {hasCustomGradingSettings(assignment) ? (
                                    <Stack direction={'row'} spacing={2}>
                                        <Text color={'light'}>Advanced settings: Custom grading instructions</Text>
                                        <Button variant={'borderless'} onClick={onClickViewGradingSettings}>
                                            View
                                        </Button>
                                    </Stack>
                                ) : (
                                    <Text color={'light'}>Advanced settings: None (system default)</Text>
                                )}
                            </CustomStack>
                        </AccordionDetails>
                    </Accordion>
                </Stack>
            </Stack>
        </>
    );
};

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '4px',
        padding: theme.spacing(3),
        background: theme.palette.ensightful.neutral[50],
    };
});

export default AssignmentSettings;
